<script setup>
// The public site header: brand, in-page nav, and the two account actions. The
// right-edge page-dot pager is a supplementary progress indicator, not primary
// navigation — it has no visible labels at rest and its active state can lag on
// long sections, so desktop still gets real named links here; LandingMenu carries
// the same set on mobile, where this nav is hidden. Solid ink-bordered bar, sticky,
// matching the AuthLayout header so the marketing surface and the app read as one
// product. Picks up the hard shadow once the page scrolls, so it reads as lifted
// off the content beneath it — the same --shadow-hard-sm vocabulary as a resting
// button, not a new effect. The bottom border doubles as a civic instrument: a
// cobalt rail fills in left-to-right with scroll progress, the same "line that
// fills" language as the money-flow diagrams.
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import BrandMark from "@/components/BrandMark.vue";
import LandingMenu from "@/components/landing/LandingMenu.vue";
import { buttonVariants } from "@/components/ui/button";
import { anchorScroll, scrollToTop } from "@/lib/scroll";
import { vMagnetic } from "@/lib/motion";

const { t } = useI18n();

// One header for the whole public site, so a page with sections of its own says
// which: `[{ href, label }]`, already in the reader's language. Omit it and the
// homepage's own table of contents is what shows.
//
// `awayLink` is the one nav item that leaves the page. From the homepage it points at
// the manager page; from the manager page it has to point back, or the header offers
// no route home at all — the brand is a scroll-to-top there, not a link.
const props = defineProps({
  anchors: { type: Array, default: null },
  awayLink: { type: Object, default: null },
});

// In document order, so the nav reads as a table of contents rather than a menu:
// "hoe het werkt" now comes before "de geldstroom" on the page itself.
const HOME_ANCHORS = [
  { href: "#hoe-het-werkt", key: "landing.nav.how" },
  { href: "#geldstroom", key: "landing.nav.flows" },
  { href: "#prijzen", key: "landing.nav.pricing" },
  { href: "#vragen", key: "landing.nav.faq" },
];

const items = computed(() => props.anchors ?? HOME_ANCHORS.map(({ href, key }) => ({ href, label: t(key) })));
const away = computed(() => props.awayLink ?? { to: "/voor-vve-beheerders", label: t("landing.nav.managers") });

// The brand means "top of this page" on the landing page and "take me home" everywhere
// else. Left as a bare scrollToTop it silently meant the first thing on both — which on
// a content page is a logo that looks like a link and does nothing.
const router = useRouter();

function onBrandClick(event) {
  if (router.currentRoute.value.path === "/") return scrollToTop(event);
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button) return;

  event.preventDefault();
  router.push("/");
}

const scrolled = ref(false);
const progress = ref(0);
let ticking = false;
let max = 0;

// Recomputed on resize only, not inside onScroll: mobile browsers change
// window.innerHeight mid-gesture as the address bar/toolbar collapses, and
// recalculating the denominator on every scroll tick made the progress rail
// visibly jump/flicker as that happened. A genuine resize still updates it —
// just not dozens of times a second while the page is merely scrolling.
function computeMax() {
  max = document.documentElement.scrollHeight - window.innerHeight;
}

function onScroll() {
  if (ticking) return;
  ticking = true;
  requestAnimationFrame(() => {
    scrolled.value = window.scrollY > 8;
    progress.value = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
    ticking = false;
  });
}

onMounted(() => {
  computeMax();
  onScroll();
  // The webfonts reflow the content-sized panels after mount, so the denominator
  // measured here is a few hundred px short until the first real resize.
  document.fonts?.ready?.then?.(computeMax);
  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", computeMax, { passive: true });
});
onUnmounted(() => {
  window.removeEventListener("scroll", onScroll);
  window.removeEventListener("resize", computeMax);
});
</script>

<template>
  <!-- bg-card/90 + backdrop-blur, not solid: the bar keeps its ink border and hard
       shadow but lets the page glow through as it scrolls underneath — lifted
       glass rather than a painted plank. -->
  <header
    class="sticky top-0 z-40 border-b-2 border-foreground bg-card/90 backdrop-blur-md transition-shadow duration-300"
    :class="scrolled ? 'shadow-[var(--shadow-hard-sm)]' : 'shadow-none'"
  >
    <!-- Fixed height, not padding-derived: every full-screen panel below sizes
         itself to `100dvh - var(--nav-h)`, so this bar has to actually BE that
         tall — border included, hence the 2px taken off the inner row. Keep the two
         in sync (--nav-h lives in application.css). -->
    <div class="mx-auto flex h-[calc(var(--nav-h)_-_2px)] w-full max-w-6xl items-center gap-4 px-5">
      <a href="/" class="-my-2 inline-flex items-center gap-2.5 py-2" aria-label="Pandig" @click="onBrandClick">
        <BrandMark class="size-6 text-primary sm:size-7" />
        <span class="font-sans text-lg font-bold tracking-tight text-foreground sm:text-xl">Pandig</span>
      </a>

      <nav class="ml-8 hidden items-center gap-6 md:flex" aria-label="Op deze pagina">
        <a
          v-for="item in items"
          :key="item.href"
          :href="item.href"
          class="text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground"
          @click="anchorScroll"
        >
          {{ item.label }}
        </a>
        <!-- The one nav item that leaves the page. lg: only — at md the four anchors,
             the brand and the two account actions already fill the bar. -->
        <RouterLink :to="away.to" class="hidden text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground lg:inline">
          {{ away.label }}
        </RouterLink>
      </nav>

      <div class="ml-auto flex items-center gap-3">
        <RouterLink to="/login" class="hidden text-sm font-semibold text-foreground underline-offset-4 hover:underline sm:inline">{{ t("landing.nav.sign_in") }}</RouterLink>
        <span v-magnetic class="hidden md:inline-block">
          <RouterLink to="/signup" :class="buttonVariants({ size: 'sm' })">{{ t("landing.nav.sign_up") }}</RouterLink>
        </span>
        <div class="md:hidden">
          <LandingMenu :anchors="anchors" :away-link="awayLink" />
        </div>
      </div>
    </div>

    <span
      class="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 bg-primary transition-[width] duration-150 ease-out"
      :style="{ width: `${progress * 100}%` }"
      aria-hidden="true"
    />
  </header>
</template>
